import React, { useState, useMemo } from "react";
import useSWR from "swr";
import BuildingIcon from "../../../svgs/building.svg";
import TypeIcon from "../../../svgs/type.svg";
import FileUpload from "../../Common/FileUpload";
import { useNotification } from "../../../../Notifications/NotificationProvider";
import {
  inventoryFetcher,
  InventoryService,
} from "../../../network/InventoryService";
import FormField from "../../../../Shared/Form/FormField";

const InventoryTransferModal = ({
  showTransferModal,
  setShowTransferModal,
  type = "warehouse",
  sourceData = null,
}) => {
  const dispatchNotification = useNotification();
  const [form, setForm] = useState({
    selectedTransferType: "",
    transferType: "",
    sourceType: type,
    sourceId: sourceData?.id?.toString() || "",
    destinationType: "",
    destination: "",
    transferReason: "",
    inventoryType: "",
    transferDate: "",
    transitType: "",
    transitVia: "",
    receiverName: "",
    receiverDesignation: "",
    receiverEmployeeId: "",
    receiverEmail: "",
    receiverPhone: "",
    courierName: "",
    courierTrackingNumber: "",
  });
  const [formError, setFormError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [destinationSearchText, setDestinationSearchText] = useState("");

  const transferTypeOptions = useMemo(() => {
    const baseOptions = [
      {
        value: "",
        label: "Select transfer type...",
        isPlaceholder: true,
      },
    ];

    if (type === "warehouse") {
      return [
        ...baseOptions,
        {
          value: "warehouse_to_warehouse_single",
          label: "Warehouse to Warehouse (Single)",
        },
        {
          value: "warehouse_to_agency_single",
          label: "Warehouse to Distribution House (Single)",
        },
        {
          value: "warehouse_to_agency_bulk",
          label: "Warehouse to Distribution House (Bulk)",
        },
      ];
    } else if (type === "agency") {
      return [
        ...baseOptions,
        {
          value: "agency_to_warehouse",
          label: "Distribution House to Warehouse",
        },
        {
          value: "agency_to_agency",
          label: "Distribution House to Distribution House",
        },
      ];
    }

    return baseOptions;
  }, [type]);

  const transitOptions = [
    { value: "", label: "Select transit modality...", isPlaceholder: true },
    { value: "hand_to_hand", label: "Hand To Hand" },
    { value: "courier", label: "Courier" },
  ];

  const inventoryTypeOptions = [
    {
      value: "",
      label: "Inventory type Device/Sim/Other...",
      isPlaceholder: true,
    },
    { value: "device", label: "Device" },
    { value: "sim", label: "Sim" },
    { value: "other", label: "Other" },
  ];

  const destinationsApiUrl = useMemo(() => {
    if (!form.transferType) return null;

    const isToWarehouse = form.transferType.includes("to_warehouse");
    const isToAgency = form.transferType.includes("to_agency");

    if (isToWarehouse) {
      const params = new URLSearchParams({
        is_dropdown: true,
      });
      if (destinationSearchText) {
        params.append("search_term", destinationSearchText);
      }
      return `${
        process.env.REACT_APP_API_URL
      }/api/payment_speaker/v1/psb-warehouse?${params.toString()}`;
    } else if (isToAgency) {
      const params = new URLSearchParams({
        is_dropdown: true,
      });
      if (destinationSearchText) {
        params.append("search_term", destinationSearchText);
      }
      return `${
        process.env.REACT_APP_API_URL
      }/api/payment_speaker/v1/psb-agency?${params.toString()}`;
    }

    return null;
  }, [form.transferType, destinationSearchText]);

  const { data: destinationsResponse, error: destinationsError } = useSWR(
    destinationsApiUrl,
    inventoryFetcher,
    {
      fallbackData: {},
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const destinations = destinationsResponse?.data || [];
  const destinationsLoading = !destinationsResponse && !destinationsError;

  const destinationOptions = destinations.map((destination) => ({
    value: destination.id.toString(),
    label: form.transferType.includes("to_warehouse")
      ? `${destination.name} - ${destination.address}`
      : `${destination.name} - ${destination.territory__name || "N/A"}`,
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "transferType") {
      let destinationType = "";
      let actualTransferType = "";
      let transitType = "";

      if (value.includes("to_warehouse")) {
        destinationType = "warehouse";
        actualTransferType = "warehouse_to_warehouse";
      } else if (value.includes("to_agency")) {
        destinationType = "agency";
        actualTransferType = "warehouse_to_agency";
      }

      if (value.includes("agency_to")) {
        transitType = "single";
      }

      if (value.includes("_single")) {
        transitType = "single";
      } else if (value.includes("_bulk")) {
        transitType = "bulk";
      }

      setForm((prev) => ({
        ...prev,
        selectedTransferType: value,
        transferType: actualTransferType,
        transitType,
        destinationType,
        destination: "",
      }));
      setDestinationSearchText("");
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDestinationSearch = (searchText) => {
    setDestinationSearchText(searchText);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];

      if (allowedTypes.includes(file.type) || file.name.endsWith(".csv")) {
        setUploadedFile(file);
      } else {
        dispatchNotification({
          type: "ERROR",
          message: "Please upload only Excel (.xlsx, .xls) or CSV files.",
        });
      }
    }
    event.target.value = "";
  };

  const handleDownloadSample = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!form.transitType) {
      dispatchNotification({
        type: "ERROR",
        message: "Please select a transfer type to download sample file.",
      });
      return;
    }

    if (!form.inventoryType) {
      dispatchNotification({
        type: "ERROR",
        message: "Please select an inventory type to download sample file.",
      });
      return;
    }

    setIsDownloading(true);
    try {
      const endpoint = `${process.env.REACT_APP_API_URL}/api/payment_speaker/upload/initiate-new-transfer?inventory_type=${form.inventoryType}&transit_type=${form.transitType}`;
      await InventoryService.downloadSampleFile(
        endpoint,
        "inventory-transfer-sample.xlsx"
      );

      dispatchNotification({
        type: "SUCCESS",
        message: "Sample file downloaded successfully!",
      });
    } catch (error) {
      console.error("Error downloading sample file:", error);
      dispatchNotification({
        type: "ERROR",
        message: "Failed to download sample file. Please try again.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.transferType ||
      !form.sourceId ||
      (!form.destination && form.transitType === "single") ||
      !form.transferReason ||
      !form.inventoryType ||
      !form.transferDate
    ) {
      setFormError("Please fill all required fields.");
      return;
    }

    if (form.transitVia === "hand_to_hand") {
      if (
        !form.receiverName ||
        !form.receiverDesignation ||
        !form.receiverEmployeeId ||
        !form.receiverEmail ||
        !form.receiverPhone
      ) {
        setFormError(
          "Please fill all receiver information for hand to hand transfer."
        );
        return;
      }
    }

    if (form.transitVia === "courier") {
      if (!form.courierName || !form.courierTrackingNumber) {
        setFormError("Please fill courier information for courier transfer.");
        return;
      }
    }

    if (!uploadedFile) {
      setFormError("Please upload an inventory file.");
      return;
    }

    setIsCreating(true);
    setFormError("");

    try {
      const transferRequestData = {
        transfer_type: form.transferType,
        source_type: form.sourceType,
        source_id: form.sourceId,
        destination_type: form.destinationType,
        destination_id: form.destination,
        transfer_reason: form.transferReason,
        inventory_type: form.inventoryType,
        handover_date: form.transferDate,
        transit_type: form.transitType,
        transit_via: form.transitVia,
        receiver_name: form.receiverName,
        receiver_designation: form.receiverDesignation,
        receiver_employee_id: form.receiverEmployeeId,
        receiver_email: form.receiverEmail,
        receiver_phone: form.receiverPhone,
        courier_name: form.courierName,
        courier_tracking_number: form.courierTrackingNumber,
        file: uploadedFile,
      };

      const response =
        await InventoryService.createInventoryTransfer(transferRequestData);

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "Inventory transfer initiated successfully!",
        });

        setForm({
          selectedTransferType: "",
          transferType: "",
          sourceType: type,
          sourceId: sourceData?.id?.toString() || "",
          destinationType: "",
          destination: "",
          transferReason: "",
          inventoryType: "",
          transferDate: "",
          transitType: "",
          transitVia: "",
          receiverName: "",
          receiverDesignation: "",
          receiverEmployeeId: "",
          receiverEmail: "",
          receiverPhone: "",
          courierName: "",
          courierTrackingNumber: "",
        });
        setUploadedFile(null);

        setShowTransferModal(false);
      } else {
        throw new Error(
          response.message || "Failed to initiate inventory transfer"
        );
      }
    } catch (error) {
      console.error("Error creating inventory transfer:", error);

      let errorMessage =
        "Failed to initiate inventory transfer. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const renderDynamicContent = () => {
    if (!form.transitVia) return null;

    const isHandToHand = form.transitVia === "hand_to_hand";
    const isCourier = form.transitVia === "courier";

    return (
      <>
        {isHandToHand && (
          <>
            <FormField
              id="receiverName"
              name="receiverName"
              title="Receiver Name"
              type="text"
              value={form.receiverName}
              onChange={handleInputChange}
              placeholder="Mr. Ahmed"
              disabled={isCreating}
              isFlat={true}
              required
            />

            <FormField
              id="receiverDesignation"
              name="receiverDesignation"
              title="Receiver Designation"
              type="text"
              value={form.receiverDesignation}
              onChange={handleInputChange}
              placeholder="Key Account Manager"
              disabled={isCreating}
              isFlat={true}
              required
            />

            <FormField
              id="receiverEmployeeId"
              name="receiverEmployeeId"
              title="Receiver Employee ID"
              type="text"
              value={form.receiverEmployeeId}
              onChange={handleInputChange}
              placeholder="PS00123"
              disabled={isCreating}
              isFlat={true}
              required
            />

            <FormField
              id="receiverEmail"
              name="receiverEmail"
              title="Receiver Email Address"
              type="email"
              value={form.receiverEmail}
              onChange={handleInputChange}
              placeholder="example@company.com"
              disabled={isCreating}
              isFlat={true}
              required
            />

            <FormField
              id="receiverPhone"
              name="receiverPhone"
              title="Receiver Phone Number"
              type="tel"
              value={form.receiverPhone}
              onChange={handleInputChange}
              placeholder="+8801XXXXXXXXX"
              disabled={isCreating}
              isFlat={true}
              required
            />
          </>
        )}

        {isCourier && (
          <>
            <FormField
              id="courierName"
              name="courierName"
              title="Courier Service Name"
              type="text"
              value={form.courierName}
              onChange={handleInputChange}
              placeholder="Sundarban Courier"
              disabled={isCreating}
              isFlat={true}
              required
            />

            <FormField
              id="courierTrackingNumber"
              name="courierTrackingNumber"
              title="CN Number"
              type="text"
              value={form.courierTrackingNumber}
              onChange={handleInputChange}
              placeholder="SCL123456789"
              disabled={isCreating}
              isFlat={true}
              required
            />
          </>
        )}
      </>
    );
  };

  return (
    <div
      className="inventory-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isCreating && !isDownloading) {
          setShowTransferModal(false);
        }
      }}
    >
      <div
        className="inventory-modal transfer-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="inventory-modal-header">
          <h3>Initiate Inventory Transfer</h3>
          <button
            className="close-btn"
            type="button"
            onClick={() =>
              !isCreating && !isDownloading && setShowTransferModal(false)
            }
            disabled={isCreating || isDownloading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="inventory-modal-body">
            <div className="inventory-modal-body-content">
              <div className="inventory-form-container">
                <FormField
                  id="transferType"
                  name="transferType"
                  title="Select Transfer Type"
                  type="select"
                  icon={BuildingIcon}
                  value={form.selectedTransferType}
                  onChange={handleInputChange}
                  options={transferTypeOptions}
                  disabled={isCreating}
                  isFlat={true}
                  required
                />

                <FormField
                  id="destination"
                  name="destination"
                  title="Select Destination"
                  type="select"
                  icon={BuildingIcon}
                  value={form.destination}
                  onChange={handleInputChange}
                  options={destinationOptions}
                  disabled={
                    isCreating ||
                    !form.transferType ||
                    destinationsLoading ||
                    form.transitType === "bulk"
                  }
                  isFlat={true}
                  required
                  searchable={true}
                  onSearch={handleDestinationSearch}
                  searchText={destinationSearchText}
                  placeholder="Select destination..."
                  loading={destinationsLoading}
                />

                <FormField
                  id="transferReason"
                  name="transferReason"
                  title="Transfer Reason"
                  type="text"
                  value={form.transferReason}
                  onChange={handleInputChange}
                  placeholder="Provide Transfer Reason"
                  disabled={isCreating}
                  isFlat={true}
                  required
                />

                <FormField
                  id="inventoryType"
                  name="inventoryType"
                  title="Inventory Type"
                  type="select"
                  icon={TypeIcon}
                  value={form.inventoryType}
                  onChange={handleInputChange}
                  options={inventoryTypeOptions}
                  disabled={isCreating}
                  isFlat={true}
                  required
                />
              </div>
              <div className="inventory-form-container">
                <FormField
                  id="transferDate"
                  name="transferDate"
                  title="Transfer Date"
                  type="date"
                  value={form.transferDate}
                  onChange={handleInputChange}
                  disabled={isCreating}
                  isFlat={true}
                  required
                />

                <FormField
                  id="transitVia"
                  name="transitVia"
                  title="Transit Via"
                  type="select"
                  icon={TypeIcon}
                  value={form.transitVia}
                  onChange={handleInputChange}
                  options={transitOptions}
                  disabled={isCreating || form.transitType === "bulk"}
                  isFlat={true}
                  required
                />
                {renderDynamicContent()}
              </div>
              <div className="transfer-upload-column">
                <FileUpload
                  setUploadedFile={setUploadedFile}
                  handleFileUpload={handleFileUpload}
                  handleFileChange={setUploadedFile}
                  handleDownloadSample={handleDownloadSample}
                  isUploading={false}
                  isDownloading={isDownloading}
                  isDisabled={isCreating || isDownloading}
                  uploadedFile={uploadedFile}
                />
              </div>
            </div>
          </div>

          {formError && <div className="inventory-form-error">{formError}</div>}

          <div className="inventory-modal-footer">
            <button
              className="cancel-btn"
              type="button"
              onClick={() => setShowTransferModal(false)}
              disabled={isCreating || isDownloading}
            >
              Cancel
            </button>
            <button
              className="submit-btn"
              type="submit"
              disabled={isCreating || isDownloading}
              onClick={(e) => {
                console.log("Submit button clicked");
              }}
            >
              {isCreating ? "Initiating Transfer..." : "Confirm Transfer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryTransferModal;
